import {
  Carousel,
  ContentLoader,
  Flex,
  Grid,
  Heading,
  Skeleton,
} from '@raskrask-dk/rr-components'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { BasisTypeBF } from '../../../models'
import { useAppSelector } from '../../../redux/hooks'
import { getIsMobile } from '../../../redux/reducers/appSettings'
import { ServicePicker } from '../../calendar/styles'
import { ItemWithHeader } from '../ItemWithHeader'
import { PageTemplate } from '../skeleton'

export const InitialLoading = ({
  basisType,
  elementsToShow,
}: {
  basisType: BasisTypeBF
  elementsToShow: {
    showGoalsLoading: boolean
    showServiceTypesLoading: boolean
    showCarouselLoading: boolean
    showPartnerCardsLoading: boolean
    showServicePickerLoading: boolean
  }
}): ReactElement => {
  const { t } = useTranslation()
  const {
    showCarouselLoading,
    showGoalsLoading,
    showPartnerCardsLoading,
    showServicePickerLoading,
    showServiceTypesLoading,
  } = elementsToShow
  const isMobile = useAppSelector((state) => getIsMobile(state))

  const daysToShow = isMobile ? 5 : 14
  const loadingCarouselArray = Array.from(Array(daysToShow + 1).keys())
  const loadingArray = Array.from(Array(3).keys())

  const goalsLoading = (
    <ItemWithHeader
      header={t('calendar.goals.title')}
      item={
        <>
          {loadingArray.map((_num, key) => (
            <Skeleton
              key={key}
              height={45}
              width={180}
              variant="rectangle"
            />
          ))}
        </>
      }
      wrapper={
        <Flex
          gap="8"
          css={{
            flexWrap: 'wrap',
            '@mobile': { flexDirection: 'column' },
          }}
        />
      }
    />
  )

  const serviceTypesLoading = (
    <ItemWithHeader
      header={t('calendar.servicetypes.title')}
      item={
        <>
          {loadingArray.map((_num, key) => (
            <Skeleton
              key={key}
              height={45}
              width={100}
              variant="rectangle"
            />
          ))}
        </>
      }
      wrapper={
        <Flex
          gap="8"
          css={{
            flexWrap: 'wrap',
            '@mobile': {
              '> *': {
                flex: '0 0 calc(33.33% - 16px / 3)',
              },
            },
          }}
        />
      }
    />
  )

  const carouselLoading = (
    <ItemWithHeader
      header={t('calendar.carousel.title')}
      item={
        <Carousel
          slidesToScroll={daysToShow}
          slidesToShow={daysToShow}
          css={{
            '.slick-arrow': {
              '@mobile': {
                h: 70,
                w: 32,
              },

              '@desktop': {
                h: 80,
                w: 40,
              },
            },
          }}
        >
          {loadingCarouselArray.map((_num, key) => (
            <Skeleton
              key={key}
              height={80}
              width={70}
              variant="rectangle"
            />
          ))}
        </Carousel>
      }
    />
  )

  const partnerCardsLoading = (
    <ItemWithHeader
      header={t('calendar.partnercards.allshown', {
        partners: t(`common:basistypes.${basisType.id}.pluralis`),
      })}
      item={
        <>
          {' '}
          {loadingArray.map((_num, key) => (
            <ContentLoader key={key} component="PartnerCard" />
          ))}
        </>
      }
      wrapper={
        <Grid
          autoFlow="dense"
          gap="16"
          css={{
            '@desktop': {
              gridTemplateColumns:
                'repeat(auto-fill, minmax(175px, 1fr))',
            },
          }}
        />
      }
    />
  )

  const servicePickerLoading = (
    <Flex
      gap={16}
      css={{
        '@desktop': {
          justifyContent: 'space-between',
          flexDirection: 'row',
        },

        '@mobile': {
          flexDirection: 'column',
        },
      }}
    >
      {showGoalsLoading ? goalsLoading : <></>}
      {showServiceTypesLoading ? serviceTypesLoading : <></>}
    </Flex>
  )

  return (
    <PageTemplate
      Top={
        <ServicePicker>
          <Heading bold primary size={4}>
            {t(basisType.t.name)}
          </Heading>
          {showServicePickerLoading ? servicePickerLoading : <></>}
          {showCarouselLoading ? carouselLoading : <></>}
        </ServicePicker>
      }
      Body={showPartnerCardsLoading ? partnerCardsLoading : <></>}
      Modal={undefined}
    />
  )
}
